import { useEffect, useMemo, useRef, useState } from 'react';
import uPlot from 'uplot';
import { formatElapsedTime, normalizeUnit } from '../lib/datalog';
import type { RangeStatistics, RenderWindowResult, TimeRange } from '../types';

export type ChartChannel = {
  id: string;
  label: string;
  unit?: string;
  color: string;
};

type TimeSeriesChartProps = {
  data: RenderWindowResult | null;
  channels: ChartChannel[];
  width: number;
  height?: number;
  visibleRange: TimeRange;
  sessionRange: TimeRange;
  cursorTime: number | null;
  autoScale: boolean;
  fixedStats: RangeStatistics[];
  onVisibleRangeChange: (range: TimeRange) => void;
  onCursorChange: (time: number) => void;
  onCursorStep: (direction: -1 | 1) => void;
};

type DragState = {
  pointerId: number;
  startX: number;
  startTime: number;
  range: TimeRange;
  moved: boolean;
};

type PlotBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export function TimeSeriesChart({
  data,
  channels,
  width,
  height = 300,
  visibleRange,
  sessionRange,
  cursorTime,
  autoScale,
  fixedStats,
  onVisibleRangeChange,
  onCursorChange,
  onCursorStep,
}: TimeSeriesChartProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const [plotBounds, setPlotBounds] = useState<PlotBounds | null>(null);
  const latest = useRef({
    visibleRange,
    sessionRange,
    onVisibleRangeChange,
    onCursorChange,
  });
  latest.current = {
    visibleRange,
    sessionRange,
    onVisibleRangeChange,
    onCursorChange,
  };

  const plotData = useMemo<uPlot.AlignedData | null>(() => {
    if (!data?.time.length || !channels.length) return null;
    const x = Array.from(data.time);
    const series = channels.map((channel) =>
      Array.from(data.values[channel.id] ?? [], (value) => (Number.isFinite(value) ? value : null)),
    );
    return [x, ...series];
  }, [channels, data]);

  useEffect(() => {
    const frame = frameRef.current;
    const host = hostRef.current;
    if (!frame || !host || !plotData || width < 160) {
      setPlotBounds(null);
      return;
    }

    const scaleByUnit = new Map<string, string>();
    const scaleChannel = new Map<string, ChartChannel>();
    for (const channel of channels) {
      const normalized = normalizeUnit(channel.unit);
      const scale = normalized ? `unit-${normalized.replace(/[^a-z0-9]+/g, '-')}` : `channel-${channel.id}`;
      scaleByUnit.set(channel.id, scale);
      if (!scaleChannel.has(scale)) scaleChannel.set(scale, channel);
    }

    const scales: uPlot.Scales = {
      x: { time: false, min: visibleRange.start, max: visibleRange.end },
    };
    for (const [scale, representative] of scaleChannel.entries()) {
      const channelsForScale = channels.filter((channel) => scaleByUnit.get(channel.id) === scale);
      const stats = fixedStats.filter((stat) => channelsForScale.some((channel) => channel.id === stat.channelId));
      const minima = stats.flatMap((stat) => (stat.min === null ? [] : [stat.min]));
      const maxima = stats.flatMap((stat) => (stat.max === null ? [] : [stat.max]));
      const min = minima.length ? Math.min(...minima) : undefined;
      const max = maxima.length ? Math.max(...maxima) : undefined;
      const padding = min !== undefined && max !== undefined
        ? Math.max((max - min) * 0.05, Math.abs(max || min || 1) * 0.005)
        : 0;
      scales[scale] = autoScale
        ? { auto: true }
        : {
            auto: false,
            min: min === undefined ? undefined : min - padding,
            max: max === undefined ? undefined : max + padding,
          };
      void representative;
    }

    const axes: uPlot.Axis[] = [
      {
        scale: 'x',
        side: 2,
        stroke: '#84909b',
        grid: { stroke: '#2a3239', width: 1 },
        ticks: { stroke: '#46515a', width: 1 },
        values: (_plot, splits) => splits.map((value) => formatElapsedTime(value)),
        font: '11px Inter, system-ui, sans-serif',
        size: 34,
      },
    ];
    [...scaleChannel.entries()].forEach(([scale, channel], index) => {
      axes.push({
        scale,
        side: index % 2 === 0 ? 3 : 1,
        stroke: channel.color,
        grid: { show: index === 0, stroke: '#222a31', width: 1 },
        ticks: { stroke: channel.color, width: 1 },
        values: (_plot, splits) => splits.map((value) => Number(value.toPrecision(5)).toString()),
        label: channel.unit || undefined,
        labelFont: '10px Inter, system-ui, sans-serif',
        font: '11px Inter, system-ui, sans-serif',
        size: 48,
        gap: 5,
      });
    });

    const series: uPlot.Series[] = [
      {},
      ...channels.map((channel) => ({
        label: channel.label,
        scale: scaleByUnit.get(channel.id),
        stroke: channel.color,
        width: 1.5,
        points: { show: false },
        spanGaps: false,
      })),
    ];

    const syncPlotBounds = (plot: uPlot) => {
      const frameRect = frame.getBoundingClientRect();
      const overRect = plot.over.getBoundingClientRect();
      setPlotBounds({
        left: overRect.left - frameRect.left,
        top: overRect.top - frameRect.top,
        width: overRect.width,
        height: overRect.height,
      });
    };

    const options: uPlot.Options = {
      width: Math.max(160, Math.floor(width)),
      height,
      padding: [12, 4, 0, 4],
      scales,
      axes,
      series,
      legend: { show: false },
      cursor: { show: false, drag: { x: false, y: false, setScale: false } },
      hooks: { setSize: [syncPlotBounds] },
    };
    const plot = new uPlot(options, plotData, host);
    let drag: DragState | null = null;
    const pointers = new Map<number, number>();
    let pinch:
      | { distance: number; midpoint: number; range: TimeRange }
      | null = null;

    const xFor = (event: PointerEvent) => {
      const rect = plot.over.getBoundingClientRect();
      return Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    };

    const handlePointerDown = (event: PointerEvent) => {
      const x = xFor(event);
      pointers.set(event.pointerId, x);
      plot.over.setPointerCapture(event.pointerId);
      if (pointers.size === 2) {
        const [left, right] = [...pointers.values()];
        pinch = {
          distance: Math.max(1, Math.abs(right - left)),
          midpoint: (left + right) / 2,
          range: { ...latest.current.visibleRange },
        };
        drag = null;
        return;
      }
      drag = {
        pointerId: event.pointerId,
        startX: x,
        startTime: plot.posToVal(x, 'x'),
        range: { ...latest.current.visibleRange },
        moved: false,
      };
    };

    const handlePointerMove = (event: PointerEvent) => {
      const x = xFor(event);
      if (pointers.has(event.pointerId)) pointers.set(event.pointerId, x);
      if (pinch && pointers.size >= 2) {
        const [left, right] = [...pointers.values()];
        const distance = Math.max(1, Math.abs(right - left));
        const factor = pinch.distance / distance;
        const baseDuration = pinch.range.end - pinch.range.start;
        const nextDuration = baseDuration * factor;
        const ratio = pinch.midpoint / Math.max(1, plot.over.clientWidth);
        const center = pinch.range.start + baseDuration * ratio;
        latest.current.onVisibleRangeChange({
          start: center - nextDuration * ratio,
          end: center + nextDuration * (1 - ratio),
        });
        event.preventDefault();
        return;
      }
      if (!drag || drag.pointerId !== event.pointerId) {
        latest.current.onCursorChange(plot.posToVal(x, 'x'));
        return;
      }
      const delta = x - drag.startX;
      if (Math.abs(delta) > 3) drag.moved = true;
      if (drag.moved) {
        const duration = drag.range.end - drag.range.start;
        const offset = (-delta / Math.max(1, plot.over.clientWidth)) * duration;
        latest.current.onVisibleRangeChange({
          start: drag.range.start + offset,
          end: drag.range.end + offset,
        });
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      const x = xFor(event);
      if (drag?.pointerId === event.pointerId && !drag.moved) {
        latest.current.onCursorChange(plot.posToVal(x, 'x'));
      }
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = null;
      if (drag?.pointerId === event.pointerId) drag = null;
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = plot.over.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
      const current = latest.current.visibleRange;
      const duration = current.end - current.start;
      const factor = event.deltaY > 0 ? 1.18 : 0.82;
      const nextDuration = duration * factor;
      const center = current.start + duration * ratio;
      latest.current.onVisibleRangeChange({
        start: center - nextDuration * ratio,
        end: center + nextDuration * (1 - ratio),
      });
    };

    plot.over.addEventListener('pointerdown', handlePointerDown);
    plot.over.addEventListener('pointermove', handlePointerMove);
    plot.over.addEventListener('pointerup', handlePointerUp);
    plot.over.addEventListener('pointercancel', handlePointerUp);
    plot.over.addEventListener('wheel', handleWheel, { passive: false });

    return () => plot.destroy();
  }, [autoScale, channels, fixedStats, height, plotData, visibleRange.end, visibleRange.start, width]);

  const cursorPercent = cursorTime === null
    ? null
    : ((cursorTime - visibleRange.start) / Math.max(0.000001, visibleRange.end - visibleRange.start)) * 100;

  return (
    <div
      ref={frameRef}
      className="chart-frame"
      role="img"
      aria-label={`Time series graph showing ${channels.map((channel) => channel.label).join(', ')}`}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          onCursorStep(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === '0') {
          event.preventDefault();
          onVisibleRangeChange(sessionRange);
        } else if (event.key === '+' || event.key === '=') {
          const middle = (visibleRange.start + visibleRange.end) / 2;
          const half = (visibleRange.end - visibleRange.start) * 0.4;
          onVisibleRangeChange({ start: middle - half, end: middle + half });
        } else if (event.key === '-') {
          const middle = (visibleRange.start + visibleRange.end) / 2;
          const half = (visibleRange.end - visibleRange.start) * 0.625;
          onVisibleRangeChange({ start: middle - half, end: middle + half });
        }
      }}
    >
      <div ref={hostRef} className="uplot-host" />
      {plotBounds && (
        <div
          className="plot-overlays"
          style={plotBounds}
          aria-hidden="true"
        >
          {cursorPercent !== null && cursorPercent >= 0 && cursorPercent <= 100 && (
            <div className="synced-cursor" style={{ left: `${cursorPercent}%` }}>
              <span>{formatElapsedTime(cursorTime!)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
